import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { useApiClient } from '@/api/ApiClientProvider'
import type { ApiClientKind } from '@/api/client'
import { RECOVERY_REASONS, type RecoveryQuery, type RecoveryReason } from '@/api/recovery'
import type { Environment } from '@/api/types'

export const WEEK_OPTIONS = [4, 8, 12] as const
export type WeekOption = (typeof WEEK_OPTIONS)[number]
export const DEFAULT_WEEKS: WeekOption = 8

/** Rows per page of the drill-down; "Show more" asks for the next page. */
export const PAGE_SIZE = 50

const WEEKS_PARAM = 'rec_weeks'
const REASON_PARAM = 'rec_reason'

export const recoveryKeys = {
  all: ['recoverable-checkouts'] as const,
  summary: (kind: ApiClientKind, query: RecoveryQuery) => ['recoverable-checkouts', 'summary', kind, query] as const,
  list: (kind: ApiClientKind, query: RecoveryQuery) => ['recoverable-checkouts', 'list', kind, query] as const,
}

function isWeekOption(value: number): value is WeekOption {
  return (WEEK_OPTIONS as readonly number[]).includes(value)
}

function isReason(value: string | null): value is RecoveryReason {
  return value !== null && (RECOVERY_REASONS as readonly string[]).includes(value)
}

/** The window and the drill-down's reason, kept in the URL so a link reproduces the view. */
export function useRecoveryParams(): {
  weeks: WeekOption
  setWeeks: (weeks: WeekOption) => void
  reason: RecoveryReason | undefined
  setReason: (reason: RecoveryReason | undefined) => void
} {
  const [params, setParams] = useSearchParams()
  const rawWeeks = Number(params.get(WEEKS_PARAM))
  const weeks = isWeekOption(rawWeeks) ? rawWeeks : DEFAULT_WEEKS
  const rawReason = params.get(REASON_PARAM)
  const reason = isReason(rawReason) ? rawReason : undefined

  const update = (param: string, value: string | undefined) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (value === undefined) next.delete(param)
        else next.set(param, value)
        return next
      },
      { replace: true },
    )

  return {
    weeks,
    setWeeks: (next) => update(WEEKS_PARAM, next === DEFAULT_WEEKS ? undefined : String(next)),
    reason,
    setReason: (next) => update(REASON_PARAM, next),
  }
}

export function useRecoverySummary(weeks: number, environments: readonly Environment[]) {
  const client = useApiClient()
  const query: RecoveryQuery = { weeks, environments }
  return useQuery({
    queryKey: recoveryKeys.summary(client.kind, query),
    queryFn: () => client.recoverableCheckoutsSummary(query),
  })
}

export function useRecoveryList(query: RecoveryQuery) {
  const client = useApiClient()
  return useQuery({
    queryKey: recoveryKeys.list(client.kind, query),
    queryFn: () => client.recoverableCheckouts(query),
    placeholderData: (previous) => previous,
  })
}
